import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Inbox, ArrowLeft, MailCheck, Archive, RotateCcw, Search } from "lucide-react";
import Logo from "../components/Logo";
import { api, formatApiError } from "../api";
import { toast } from "sonner";

const inputCls =
    "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100 transition-all duration-300";

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem("aus_admin_token") || "");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [enquiries, setEnquiries] = useState(null);
    const [referrers, setReferrers] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [prices, setPrices] = useState([]);
    const [view, setView] = useState("enquiries");
    const [priceQ, setPriceQ] = useState("");
    const [filter, setFilter] = useState("all");

    const loadEnquiries = (tok) => {
        const headers = { Authorization: `Bearer ${tok}` };
        api
            .get("/admin/enquiries", { headers })
            .then((r) => setEnquiries(r.data))
            .catch((e) => {
                if (e?.response?.status === 401) logout();
                else toast.error("Could not load enquiries");
            });
        api
            .get("/admin/referrers", { headers })
            .then((r) => setReferrers(r.data))
            .catch(() => {});
        api
            .get("/admin/reviews", { headers })
            .then((r) => setReviews(r.data))
            .catch(() => {});
        api
            .get("/admin/prices", { headers })
            .then((r) => setPrices(r.data))
            .catch(() => {});
    };

    const reviewAction = async (id, status) => {
        try {
            await api.patch(`/admin/reviews/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
            setReviews((list) => list.map((r) => (r.id === id ? { ...r, status } : r)));
            toast.success(status === "approved" ? "Review published" : "Review rejected");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => {
        if (token) loadEnquiries(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("aus_admin_token", data.token);
            setToken(data.token);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem("aus_admin_token");
        setToken("");
        setEnquiries(null);
    };

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/enquiries/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
            setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
            toast.success(`Marked as ${status}`);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const STATUS_STYLES = {
        new: "bg-cyan-50 text-cyan-800 border-cyan-200",
        contacted: "bg-amber-50 text-amber-800 border-amber-200",
        closed: "bg-slate-100 text-slate-500 border-slate-200",
    };

    const counts = (enquiries || []).reduce(
        (acc, e) => {
            const s = e.status || "new";
            acc[s] = (acc[s] || 0) + 1;
            return acc;
        },
        { new: 0, contacted: 0, closed: 0 }
    );
    const visible = (enquiries || []).filter((e) => filter === "all" || (e.status || "new") === filter);

    return (
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-page">
            <header className="border-b border-slate-200 bg-white/85 backdrop-blur-md">
                <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Logo className="w-8 h-8" />
                        <span className="font-display font-bold text-slate-900">
                            Aus<span className="text-cyan-700">Research</span>
                            <span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 ml-3">Admin</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Link to="/" data-testid="admin-back-link" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors">
                            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Site</span>
                        </Link>
                        {token && (
                            <button
                                data-testid="admin-logout-button"
                                onClick={logout}
                                className="flex items-center gap-1.5 text-sm font-medium border border-slate-200 rounded-full px-3.5 py-2 text-slate-600 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors duration-300"
                            >
                                <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span>
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {!token ? (
                <div className="flex items-center justify-center px-4 py-24">
                    <motion.form
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        onSubmit={login}
                        data-testid="admin-login-form"
                        className="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-8 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)]"
                    >
                        <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Admin sign in</h1>
                        <p className="text-sm text-slate-500 mt-1 mb-6">Access the institutional enquiry inbox.</p>
                        <div className="space-y-4">
                            <div>
                                <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Email</label>
                                <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Password</label>
                                <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
                            </div>
                            {error && (
                                <div data-testid="admin-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                                    {error}
                                </div>
                            )}
                            <button
                                data-testid="admin-login-button"
                                type="submit"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white font-medium py-3.5 rounded-full hover:bg-cyan-800 disabled:opacity-60 transition-colors duration-300"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                {loading ? "Signing in…" : "Sign in"}
                            </button>
                        </div>
                    </motion.form>
                </div>
            ) : (
                <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-12">
                    <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
                        <div>
                            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mb-2">
                                {view === "enquiries" ? "Enquiry inbox" : "Wholesale price list"}
                            </h1>
                            <p className="text-sm text-slate-500">
                                {view === "enquiries"
                                    ? "Institutional research enquiries submitted through the site."
                                    : "Private quoting reference — never shown on the public site."}
                            </p>
                        </div>
                        <div className="flex gap-2" data-testid="admin-view-tabs">
                            <button
                                data-testid="view-tab-enquiries"
                                onClick={() => setView("enquiries")}
                                className={`font-jbm text-xs px-4 py-2 rounded-full border transition-colors duration-300 ${
                                    view === "enquiries" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                                }`}
                            >
                                Enquiries
                            </button>
                            <button
                                data-testid="view-tab-prices"
                                onClick={() => setView("prices")}
                                className={`font-jbm text-xs px-4 py-2 rounded-full border transition-colors duration-300 ${
                                    view === "prices" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                                }`}
                            >
                                Price list
                            </button>
                        </div>
                    </div>

                    {view === "prices" ? (
                        <div data-testid="price-list-view">
                            <div className="relative mb-3 max-w-md">
                                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    data-testid="price-search-input"
                                    value={priceQ}
                                    onChange={(e) => setPriceQ(e.target.value)}
                                    placeholder="Search compound…"
                                    className="w-full bg-white border border-slate-200 rounded-full pl-11 pr-4 py-3 text-sm outline-none focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100 transition-all duration-300"
                                />
                            </div>
                            <p className="font-jbm text-xs text-slate-400 mb-6">All prices USD per 10-vial pack — WHOLESALE is supplier cost, +50% is wholesale × 1.5.</p>
                            <div className="grid md:grid-cols-2 gap-4">
                                {prices
                                    .filter((p) => !priceQ.trim() || p.name.toLowerCase().includes(priceQ.trim().toLowerCase()))
                                    .map((p) => (
                                        <div key={p.slug} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`price-row-${p.slug}`}>
                                            <div className="flex items-baseline justify-between gap-2 mb-3">
                                                <span className="font-display font-semibold text-slate-900">{p.name}</span>
                                                <span className="font-jbm text-[10px] uppercase tracking-widest text-cyan-700">{p.category}</span>
                                            </div>
                                            {p.tiers.length === 0 ? (
                                                <p className="font-jbm text-xs text-slate-400">Not on the supplier price list</p>
                                            ) : (
                                                <table className="w-full text-xs font-jbm">
                                                    <thead>
                                                        <tr className="text-slate-400 text-left">
                                                            <th className="pb-1.5 font-medium">SIZE</th>
                                                            <th className="pb-1.5 font-medium text-right">WHOLESALE</th>
                                                            <th className="pb-1.5 font-medium text-right">+50%</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {p.tiers.map(([size, w, r]) => (
                                                            <tr key={size} className="border-t border-slate-100">
                                                                <td className="py-1.5 text-slate-700">{size}</td>
                                                                <td className="py-1.5 text-right text-slate-500">${Number(w).toFixed(2)}</td>
                                                                <td className="py-1.5 text-right text-slate-900 font-semibold">${Number(r).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ) : enquiries === null ? (
                        <div className="py-20 text-center font-jbm text-sm text-slate-400">Loading…</div>
                    ) : (
                        <>
                            {referrers.length > 0 && (
                                <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-8" data-testid="referrers-summary">
                                    <div className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mb-4">
                                        Referral codes — {referrers.reduce((s, r) => s + r.uses, 0)} referred enquir{referrers.reduce((s, r) => s + r.uses, 0) === 1 ? "y" : "ies"}
                                    </div>
                                    <div className="space-y-2.5">
                                        {referrers.map((r) => (
                                            <div key={r.code} className="flex items-center gap-3 flex-wrap text-sm" data-testid={`referrer-row-${r.code}`}>
                                                <span className="font-jbm font-semibold text-slate-900 tracking-widest">{r.code}</span>
                                                <span className="text-slate-600">{r.name}</span>
                                                <span className="text-slate-400 text-xs">{r.email}</span>
                                                <span className={`ml-auto font-jbm text-[10px] uppercase tracking-widest border rounded-full px-2.5 py-1 ${r.uses > 0 ? "bg-cyan-50 text-cyan-800 border-cyan-200" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                                                    {r.uses} use{r.uses === 1 ? "" : "s"}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {reviews.filter((r) => r.status === "pending").length > 0 && (
                                <div className="bg-amber-50 border border-amber-300 rounded-2xl p-6 mb-8" data-testid="reviews-pending">
                                    <div className="font-jbm text-[10px] uppercase tracking-widest text-amber-700 mb-4">
                                        Reviews awaiting your approval — {reviews.filter((r) => r.status === "pending").length}
                                    </div>
                                    <div className="space-y-3">
                                        {reviews.filter((r) => r.status === "pending").map((r) => (
                                            <div key={r.id} className="bg-white border border-amber-200 rounded-xl p-4" data-testid={`review-pending-${r.id}`}>
                                                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                                                    <span className="font-display font-semibold text-slate-900 text-sm">{r.name}</span>
                                                    <span className="font-jbm text-xs text-amber-700">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                                                </div>
                                                <p className="text-sm text-slate-600 leading-relaxed mb-3">{r.text}</p>
                                                <div className="flex gap-2">
                                                    <button
                                                        data-testid={`review-approve-${r.id}`}
                                                        onClick={() => reviewAction(r.id, "approved")}
                                                        className="text-xs font-medium border border-slate-200 rounded-full px-4 py-1.5 text-slate-600 hover:bg-cyan-700 hover:border-cyan-700 hover:text-white transition-colors duration-300"
                                                    >
                                                        Approve & publish
                                                    </button>
                                                    <button
                                                        data-testid={`review-reject-${r.id}`}
                                                        onClick={() => reviewAction(r.id, "rejected")}
                                                        className="text-xs font-medium border border-slate-200 rounded-full px-4 py-1.5 text-slate-600 hover:bg-red-600 hover:border-red-600 hover:text-white transition-colors duration-300"
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {enquiries.length === 0 ? (
                                <div className="py-20 text-center" data-testid="admin-empty-state">
                                    <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                                    <p className="font-display text-xl text-slate-700">No enquiries yet</p>
                                    <p className="text-sm text-slate-500 mt-2">New submissions will appear here.</p>
                                </div>
                            ) : (
                            <>
                            <div className="flex gap-2 mb-6 flex-wrap" data-testid="inbox-filter-tabs">
                                {[
                                    ["all", "All", enquiries.length],
                                    ["new", "New", counts.new],
                                    ["contacted", "Contacted", counts.contacted],
                                    ["closed", "Closed", counts.closed],
                                ].map(([key, label, count]) => (
                                    <button
                                        key={key}
                                        data-testid={`filter-tab-${key}`}
                                        onClick={() => setFilter(key)}
                                        className={`flex items-center gap-2 font-jbm text-xs px-4 py-2 rounded-full border transition-colors duration-300 ${
                                            filter === key
                                                ? "bg-slate-900 text-white border-slate-900"
                                                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                                        }`}
                                    >
                                        {label}
                                        <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${filter === key ? "bg-white/20" : "bg-slate-100"}`}>
                                            {count}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            {visible.length === 0 ? (
                                <div className="py-16 text-center" data-testid="admin-filter-empty">
                                    <p className="font-display text-lg text-slate-700">No {filter} enquiries</p>
                                    <p className="text-sm text-slate-500 mt-1">{filter === "all" ? "No enquiries match." : `No ${filter} enquiries — switch tabs to see the rest of the inbox.`}</p>
                                </div>
                            ) : (
                        <div className="space-y-4" data-testid="admin-enquiries-list">
                            {visible.map((enq) => (
                                <div key={enq.id} className={`bg-white border border-slate-200 rounded-2xl p-6 ${enq.status === "closed" ? "opacity-60" : ""}`} data-testid={`admin-enquiry-${enq.id}`}>
                                    <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="font-display font-semibold text-slate-900">{enq.name}</span>
                                            <span className="text-sm text-slate-500">{enq.email}</span>
                                            <span
                                                data-testid={`status-badge-${enq.id}`}
                                                className={`font-jbm text-[10px] uppercase tracking-widest border rounded-full px-2.5 py-1 ${STATUS_STYLES[enq.status] || STATUS_STYLES.new}`}
                                            >
                                                {enq.status || "new"}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {enq.status !== "contacted" && (
                                                <button
                                                    data-testid={`mark-contacted-${enq.id}`}
                                                    onClick={() => setStatus(enq.id, "contacted")}
                                                    className="flex items-center gap-1.5 text-xs font-medium border border-slate-200 rounded-full px-3.5 py-1.5 text-slate-600 hover:bg-amber-500 hover:border-amber-500 hover:text-white transition-colors duration-300"
                                                >
                                                    <MailCheck className="w-3.5 h-3.5" /> Mark contacted
                                                </button>
                                            )}
                                            {enq.status !== "closed" && (
                                                <button
                                                    data-testid={`mark-closed-${enq.id}`}
                                                    onClick={() => setStatus(enq.id, "closed")}
                                                    className="flex items-center gap-1.5 text-xs font-medium border border-slate-200 rounded-full px-3.5 py-1.5 text-slate-600 hover:bg-slate-900 hover:border-slate-900 hover:text-white transition-colors duration-300"
                                                >
                                                    <Archive className="w-3.5 h-3.5" /> Close
                                                </button>
                                            )}
                                            {enq.status !== "new" && (
                                                <button
                                                    data-testid={`mark-new-${enq.id}`}
                                                    onClick={() => setStatus(enq.id, "new")}
                                                    aria-label="Reopen enquiry"
                                                    className="flex items-center gap-1.5 text-xs font-medium border border-slate-200 rounded-full px-3.5 py-1.5 text-slate-600 hover:bg-cyan-700 hover:border-cyan-700 hover:text-white transition-colors duration-300"
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5" /> Reopen
                                                </button>
                                            )}
                                            <span className="font-jbm text-xs text-slate-400 ml-1">
                                                {enq.created_at ? new Date(enq.created_at).toLocaleString("en-AU") : ""}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm text-slate-600">
                                        <div><span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mr-2">Institution</span>{enq.institution}</div>
                                        <div><span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mr-2">Role</span>{enq.role}</div>
                                        <div><span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mr-2">Quantity</span>{enq.quantity || "—"}</div>
                                        <div className="sm:col-span-2">
                                            <span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mr-2">Compounds</span>
                                            {(enq.compounds || []).join(", ")}
                                        </div>
                                        {enq.referred_by && (
                                            <div className="sm:col-span-2">
                                                <span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mr-2">Referred by</span>
                                                <span className="text-cyan-800 font-medium">{enq.referred_by}</span>
                                            </div>
                                        )}
                                        <div>
                                            <span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mr-2">COAs</span>
                                            {enq.wants_coa ? (
                                                <span className="text-cyan-800 font-medium">Yes — attach with reply</span>
                                            ) : (
                                                <span className="text-slate-500">No</span>
                                            )}
                                        </div>
                                        <div className="sm:col-span-2">
                                            <span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400 mr-2">Purpose</span>
                                            {enq.purpose}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                            )}
                            </>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
